/**
 * «Установить приложение» — CRM на экране телефона (план 110, запрос владельца).
 *
 * ЗАЧЕМ. Ярлык на домашнем экране открывает CRM без адресной строки, а на iOS
 * это ещё и ЕДИНСТВЕННЫЙ способ получать push: во вкладке Safari их нет вовсе.
 *
 * ДВА ПУТИ, ПОТОМУ ЧТО ПЛАТФОРМЫ РАЗНЫЕ. Chrome/Android даёт событие
 * `beforeinstallprompt` — показываем родной диалог одной кнопкой. Safari такого
 * события не имеет: там установка только руками, и всё, что можно сделать
 * полезного, — показать точные шаги. Выдумывать кнопку, которая на iPhone
 * ничего не делает, нельзя: это худший вид неработающего интерфейса.
 */
import { useState } from 'react';

import { canPromptInstall, isIOS, isStandalone, promptInstall } from '@/lib/pwa';

export function InstallAppBlock(): JSX.Element | null {
  const [done, setDone] = useState(false);

  // Уже установлено — предлагать нечего.
  if (isStandalone() || done) return null;

  if (canPromptInstall()) {
    return (
      <div className="rounded-card border border-divider-gray p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-sm font-medium text-ink-black">Установить приложение</div>
            <p className="mt-1 text-sm text-muted-text">
              CRM появится на экране телефона и будет открываться без адресной строки.
            </p>
          </div>
          <button
            type="button"
            onClick={() => void promptInstall().then((ok) => setDone(ok))}
            className="shrink-0 rounded-full bg-blueprint-blue px-4 py-2 text-sm text-canvas-white"
          >
            Установить
          </button>
        </div>
      </div>
    );
  }

  if (isIOS()) {
    return (
      <div className="rounded-card border border-divider-gray p-4">
        <div className="text-sm font-medium text-ink-black">Добавить CRM на экран «Домой»</div>
        <ol className="mt-2 list-decimal space-y-1 pl-5 text-sm text-muted-text">
          <li>Нажмите «Поделиться» — квадрат со стрелкой вверх внизу экрана.</li>
          <li>Выберите «На экран „Домой“».</li>
          <li>Откройте CRM с появившегося ярлыка.</li>
        </ol>
        <p className="mt-2 text-sm text-muted-text">
          После этого можно включить уведомления — на iPhone они работают только в установленном
          приложении.
        </p>
      </div>
    );
  }

  // Десктоп или браузер без установки — блок не показываем вовсе.
  return null;
}
